import type { UseSeoMetaInput } from '@unhead/vue'
import type { ResponsiveImageSource } from '~/types/images.ts'

interface ExtendedUseSeoMetaInput extends UseSeoMetaInput {
  title: string
  description: string
  cachedAt?: string
}

interface ExtendedUseSeoMetaOutput extends UseSeoMetaInput {
  title: () => string
  cachedAt: string
}

export function getMetaTags(
  meta: Partial<ExtendedUseSeoMetaInput>,
  imageSrc?: ResponsiveImageSource,
  baseKey?: string
): Partial<ExtendedUseSeoMetaOutput> {
  const { t, te } = useI18n(baseKey)
  const localeStore = useLocaleStore()
  const route = useRoute()

  let metaTags: Partial<ExtendedUseSeoMetaOutput> = {
    title: () => {
      if (!meta.title) {
        return ''
      }

      return te(meta.title) ? t(meta.title) : meta.title
    },
    ogUrl: new URL(route.path, 'https://thuisgekookt.nl').toString()
  }

  const description =
    meta.description && te(meta.description) ? t(meta.description) : meta.description

  if (description) {
    metaTags = {
      ...metaTags,
      description: description,

      // Twitter
      twitterCard: 'summary_large_image',
      twitterSite: '@thuisgekookt',

      // Open Graphs
      ogTitle: metaTags.title,
      ogDescription: description,
      ogLocale: () => (localeStore.localeIsDutch ? 'nl_NL' : 'en_GB'),
      ogSiteName: () =>
        localeStore.localeIsDutch
          ? 'Stichting Thuisgekookt'
          : 'The Thuisgekookt Foundation',
      ogType: 'website'
    }
  }

  const imageUrl = imageSrc ? getImageSource(imageSrc) : undefined
  if (imageUrl) {
    const img = useImage()

    const ipxImageUrl = img(imageUrl, {
      width: 300,
      height: 157
    })

    metaTags.ogImage = {
      url: new URL(ipxImageUrl, 'https://thuisgekookt.nl').toString(),
      alt: localeStore.localeIsDutch
        ? 'Stichting Thuisgekookt cover afbeelding'
        : 'The Thuisgekookt Foundation cover image'
    }
  }

  if (meta.cachedAt) {
    metaTags.cachedAt = meta.cachedAt
  }

  return metaTags
}
